import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Label,
} from '@repo/shadcn';
import { useDebounce } from '@uidotdev/usehooks';
import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { PiPlusCircleDuotone } from 'react-icons/pi';

import { AutoCompleteInput } from '../../../../components/ui/autocomplete/auto-complete-input';
import { MODAL_ROUTE } from '../../../../constants';
import { useGetTypesQuery } from '../../../../features/slice/products/type/type-api-slice';
import { useModal } from '../../../../hooks';
import { TProductForm } from './product-form.component';

export const TypeSelector: FC = () => {
    const [searchInputText, setSearchInputText] = useState('');
    const { openModal } = useModal();

    const { setValue, watch, control } = useFormContext<TProductForm>();

    const selectedTypeId = watch('type_id');
    const selectedTypeName = watch('type_name');

    const debouncedSearchInputText = useDebounce(searchInputText, 500);

    const { data, isLoading, isFetching } = useGetTypesQuery({
        search: debouncedSearchInputText,
    });

    const handleInputChange = (value: string) => {
        setSearchInputText(value);
    };

    const handleValueChange = (value: any) => {
        if (!value) {
            setValue('type_id', '');
            setValue('type_name', '');
            return;
        }

        setValue('type_id', value.value);
        setValue('type_name', value.label);
    };

    const generateOptions = () => {
        const options =
            data?.data.map((brand: any) => ({
                label: brand.name,
                value: brand.id,
            })) || [];

        // add selected option if not in the list
        if (
            selectedTypeId &&
            selectedTypeName &&
            !options.find((opt: any) => opt.value === selectedTypeId)
        ) {
            options.unshift({
                label: selectedTypeName,
                value: selectedTypeId,
            });
        }

        return options;
    };

    const options = generateOptions();

    const selectedOption = {
        label: selectedTypeName || '',
        value: selectedTypeId || '',
    };

    return (
        <FormField
            control={control}
            name={'type_id'}
            render={() => (
                <FormItem>
                    <FormLabel className="flex items-center justify-between">
                        <div>
                            <span>Type</span>
                            <span className="ml-1 text-red-500">*</span>
                        </div>

                        <Label
                            onClick={() =>
                                openModal(MODAL_ROUTE.CREATE_PRODUCT_TYPE)
                            }
                            className="flex cursor-pointer items-center gap-1 text-primary hover:underline"
                        >
                            <PiPlusCircleDuotone size={16} className="-mt-1" />
                            Add Type
                        </Label>
                    </FormLabel>

                    <FormControl>
                        <AutoCompleteInput
                            isLoading={isLoading || isFetching}
                            options={options}
                            value={selectedOption}
                            emptyMessage={'No items found'}
                            onValueChange={handleValueChange}
                            onInputChange={handleInputChange}
                            showSuggestionsOnFocus={true}
                        />
                    </FormControl>
                    <FormMessage className="line-clamp-1 text-xs" />
                </FormItem>
            )}
        />
    );
};

TypeSelector.displayName = 'TypeSelector';
